// src/pages/admin-audit-management/components/FilePreviewModal.jsx
import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabaseAdmin } from '../../../lib/supabase';
import toast from 'react-hot-toast';

/**
 * FilePreviewModal — UI matches target screenshot exactly:
 *
 *  ┌──────────────────────────────────────────────────────────────┐
 *  │  filename.html   🔥 Paid                          <>   ✕   │  ← white header
 *  │  Assigned to: Nitesh Tembe                                   │
 *  │  Company: Dipak                                              │
 *  │  Audit ID: 70d648f2-...                                      │
 *  ├──────────────────────────────────────────────────────────────┤
 *  │                                                              │
 *  │            [ scrollable iframe / preview ]                   │
 *  │                                                              │
 *  ├──────────────────────────────────────────────────────────────┤
 *  │  ↓ Download   ↺ Refresh            ✕ Cancel  🚀 Publish    │
 *  └──────────────────────────────────────────────────────────────┘
 */

const FilePreviewModal = ({ file, isOpen, onClose, onPublish }) => {
  const [htmlContent, setHtmlContent] = useState(null);
  const [isLoading,   setIsLoading]   = useState(false);
  const [error,       setError]       = useState(null);
  const [viewMode,    setViewMode]    = useState('preview'); // 'preview' | 'raw'

  // ── Load HTML whenever modal opens with a file ────────────────────────────
  useEffect(() => {
    if (isOpen && file) {
      loadHtmlContent();
    } else {
      setHtmlContent(null);
      setError(null);
      setIsLoading(false);
      setViewMode('preview');
    }
  }, [isOpen, file]);

  // ── Escape key ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // ── Lock body scroll ──────────────────────────────────────────────────────
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // ── HTML loading (unchanged logic, same 3-strategy approach) ─────────────
  const loadHtmlContent = async () => {
    if (!file) return;
    setIsLoading(true);
    setError(null);

    try {
      // Strategy 1: inline htmlContent
      if (file.htmlContent) {
        setHtmlContent(file.htmlContent);
        return;
      }

      // Strategy 2: storage download / signed URL
const storagePath = file.storage_path || file.full_path || file.html_storage_path || file.storagePath;      if (storagePath && supabaseAdmin) {
        const { data, error: dlErr } = await supabaseAdmin
          .storage.from('html-reports').download(storagePath);
        if (!dlErr && data) { setHtmlContent(await data.text()); return; }

        const { data: sd, error: se } = await supabaseAdmin
          .storage.from('html-reports').createSignedUrl(storagePath, 3600);
        if (!se && sd?.signedUrl) {
          const res = await fetch(sd.signedUrl);
          if (res.ok) { setHtmlContent(await res.text()); return; }
        }
      }

      // Strategy 3: public URL fetch
      const publicUrl = file.viewUrl || file.public_url || file.html_url;
      if (publicUrl) {
        const res = await fetch(publicUrl);
        if (res.ok) { setHtmlContent(await res.text()); return; }
      }

      throw new Error('Could not load HTML content from any source');
    } catch (err) {
      console.error('Error loading HTML:', err);
      setError(err.message);
      toast.error('Failed to load HTML preview');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Download ──────────────────────────────────────────────────────────────
  const handleDownload = async () => {
    if (!htmlContent) { toast.error('No content to download'); return; }
    try {
      const blob = new Blob([htmlContent], { type: 'text/html; charset=utf-8' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = file?.name || file?.file_name || 'audit-report.html';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Download started');
    } catch (err) {
      toast.error('Failed to download file');
    }
  };

  // ── Publish ───────────────────────────────────────────────────────────────
  const handlePublish = () => {
    if (!file?.auditId && !file?.id) { toast.error('Cannot publish: Missing audit ID'); return; }
    onPublish(file);
  };

  if (!isOpen || !file) return null;

  const displayName = file?.name || file?.file_name || 'Audit Report Preview';
  const assignedTo  = file?.userName || file?.user_profiles?.full_name || 'Unknown User';
  const isPaid      = file?.auditType === 'paid';

  return (
    <>
      {/* ── Backdrop ──────────────────────────────────────────────────────── */}
      <div
        onClick={onClose}
        aria-hidden="true"
        style={{
          position: 'fixed', inset: 0, zIndex: 50,
          background: 'rgba(0,0,0,0.45)',
          backdropFilter: 'blur(3px)',
        }}
      />

      {/* ── Modal container ───────────────────────────────────────────────── */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Preview: ${displayName}`}
        style={{
          position: 'fixed', inset: 0, zIndex: 51,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '16px',
          pointerEvents: 'none',
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            pointerEvents: 'auto',
            width: '100%',
            maxWidth: '820px',
            maxHeight: '90vh',
            background: '#ffffff',
            borderRadius: '16px',
            boxShadow: '0 32px 80px rgba(0,0,0,0.22), 0 4px 20px rgba(0,0,0,0.10)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >

          {/* ── HEADER ──────────────────────────────────────────────────── */}
          <div style={{
            padding: '18px 20px 14px',
            borderBottom: '1px solid #e2e8f0',
            flexShrink: 0,
            background: '#ffffff',
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>

              {/* Left: filename + badge */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  {/* Filename */}
                  <h2 style={{
                    margin: 0,
                    fontSize: '15px',
                    fontWeight: 600,
                    color: '#0f172a',
                    lineHeight: 1.4,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '100%',
                  }} title={displayName}>
                    {displayName}
                  </h2>

                  {/* Paid / Free badge */}
                  {file?.auditType && (
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '2px 8px',
                      borderRadius: '20px',
                      fontSize: '11px',
                      fontWeight: 600,
                      flexShrink: 0,
                      ...(isPaid
                        ? { background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' }
                        : { background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' }),
                    }}>
                      {isPaid ? '🔥' : '✦'} {isPaid ? 'Paid' : 'Free'}
                    </span>
                  )}
                </div>

                {/* Assigned to */}
                <p style={{ margin: '5px 0 0', fontSize: '13px', color: '#64748b' }}>
                  Assigned to:{' '}
                  <span style={{ color: '#0f172a', fontWeight: 500 }}>{assignedTo}</span>
                </p>

                {/* Company */}
                {file?.company_name && (
                  <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#3b82f6' }}>
                    Company: {file.company_name}
                  </p>
                )}

                {/* Audit ID */}
                {file?.auditId && (
                  <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#94a3b8', fontFamily: 'monospace' }}>
                    Audit ID: {file.auditId}
                  </p>
                )}
              </div>

              {/* Right: toggle code view + close */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, marginTop: 1 }}>
                {/* Code toggle */}
                <button
                  onClick={() => setViewMode(v => v === 'preview' ? 'raw' : 'preview')}
                  title={viewMode === 'preview' ? 'View raw HTML' : 'View preview'}
                  style={{
                    width: 32, height: 32,
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    background: viewMode === 'raw' ? '#f1f5f9' : '#fff',
                    cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#64748b',
                    transition: 'background 0.15s, color 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#0f172a'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = viewMode === 'raw' ? '#f1f5f9' : '#fff'; e.currentTarget.style.color = '#64748b'; }}
                >
                  <Icon name={viewMode === 'preview' ? 'Code' : 'Eye'} size={15} />
                </button>

                {/* Close */}
                <button
                  onClick={onClose}
                  aria-label="Close"
                  style={{
                    width: 32, height: 32,
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    background: '#fff',
                    cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#64748b',
                    transition: 'background 0.15s, color 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.color = '#ef4444'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#64748b'; }}
                >
                  <Icon name="X" size={15} />
                </button>
              </div>
            </div>
          </div>

          {/* ── BODY / PREVIEW AREA ──────────────────────────────────────── */}
          <div style={{
            flex: 1,
            overflow: 'hidden',
            position: 'relative',
            background: '#ffffff',
            minHeight: '420px',
          }}>

            {/* Loading */}
            {isLoading && (
              <div style={{
                position: 'absolute', inset: 0, zIndex: 10,
                background: '#fff',
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 12,
              }}>
                <div style={{
                  width: 34, height: 34,
                  border: '3px solid #e2e8f0',
                  borderTopColor: '#3b82f6',
                  borderRadius: '50%',
                  animation: 'fpm-spin 0.75s linear infinite',
                }} />
                <p style={{ fontSize: '13px', color: '#94a3b8', margin: 0 }}>Loading preview…</p>
              </div>
            )}

            {/* Error */}
            {!isLoading && error && (
              <div style={{
                position: 'absolute', inset: 0, zIndex: 10,
                background: '#fff',
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 12,
                padding: 24, textAlign: 'center',
              }}>
                <div style={{
                  width: 48, height: 48, borderRadius: '50%',
                  background: '#fef2f2',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon name="AlertTriangle" size={22} style={{ color: '#ef4444' }} />
                </div>
                <div>
                  <p style={{ margin: '0 0 4px', fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>
                    Preview Unavailable
                  </p>
                  <p style={{ margin: 0, fontSize: '12px', color: '#94a3b8', maxWidth: 320 }}>{error}</p>
                </div>
                <button
                  onClick={loadHtmlContent}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '7px 14px', borderRadius: 8,
                    border: '1px solid #e2e8f0', background: '#fff',
                    fontSize: '13px', fontWeight: 500, color: '#374151',
                    cursor: 'pointer',
                  }}
                >
                  <Icon name="RefreshCw" size={13} /> Retry
                </button>
              </div>
            )}

            {/* No content */}
            {!isLoading && !error && !htmlContent && (
              <div style={{
                position: 'absolute', inset: 0,
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 8,
                background: '#fff',
              }}>
                <div style={{
                  width: 48, height: 48, borderRadius: '50%',
                  background: '#f1f5f9',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon name="FileText" size={22} style={{ color: '#94a3b8' }} />
                </div>
                <p style={{ margin: 0, fontSize: '13px', color: '#94a3b8' }}>No preview available</p>
              </div>
            )}

            {/* Preview iframe — flush, no wrapper gap */}
            {!isLoading && !error && htmlContent && viewMode === 'preview' && (
              <iframe
                srcDoc={htmlContent}
                title="Audit Report Preview"
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  display: 'block',
                  background: '#fff',
                }}
                sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
              />
            )}

            {/* Raw HTML code view */}
            {!isLoading && !error && htmlContent && viewMode === 'raw' && (
              <pre style={{
                margin: 0,
                padding: '16px',
                height: '100%',
                overflow: 'auto',
                background: '#0f172a',
                color: '#e2e8f0',
                fontSize: '11px',
                fontFamily: 'ui-monospace, "Cascadia Code", monospace',
                lineHeight: 1.6,
              }}>
                {htmlContent}
              </pre>
            )}
          </div>

          {/* ── FOOTER ───────────────────────────────────────────────────── */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '13px 20px',
            borderTop: '1px solid #e2e8f0',
            background: '#ffffff',
            flexShrink: 0,
          }}>

            {/* Left: Download + Refresh */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FooterBtn
                icon="Download"
                label="Download"
                onClick={handleDownload}
                disabled={!htmlContent}
              />
              <FooterBtn
                icon="RefreshCw"
                label="Refresh"
                onClick={loadHtmlContent}
                disabled={isLoading}
              />
            </div>

            {/* Right: Cancel + Publish */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {/* Cancel — text only, no border */}
              <button
                onClick={onClose}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  padding: '8px 12px',
                  background: 'none', border: 'none',
                  borderRadius: 8,
                  fontSize: '13px', fontWeight: 400, color: '#64748b',
                  cursor: 'pointer',
                  transition: 'color 0.15s',
                  whiteSpace: 'nowrap',
                }}
                onMouseEnter={e => e.currentTarget.style.color = '#0f172a'}
                onMouseLeave={e => e.currentTarget.style.color = '#64748b'}
              >
                <Icon name="X" size={13} />
                Cancel
              </button>

              {/* Publish to User — dark navy gradient */}
              {file.status !== 'published' && (
                <button
                  onClick={handlePublish}
                  disabled={!file?.auditId && !file?.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '9px 20px',
                    borderRadius: 9,
                    border: 'none',
                    background: (!file?.auditId && !file?.id)
                      ? '#94a3b8'
                      : 'linear-gradient(135deg, #1e3a5f 0%, #1d4ed8 100%)',
                    boxShadow: (!file?.auditId && !file?.id)
                      ? 'none'
                      : '0 2px 10px rgba(29,78,216,0.32)',
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: (!file?.auditId && !file?.id) ? 'not-allowed' : 'pointer',
                    opacity: (!file?.auditId && !file?.id) ? 0.6 : 1,
                    transition: 'opacity 0.15s, box-shadow 0.15s',
                    whiteSpace: 'nowrap',
                  }}
                  onMouseEnter={e => {
                    if (file?.auditId || file?.id) {
                      e.currentTarget.style.boxShadow = '0 4px 18px rgba(29,78,216,0.45)';
                      e.currentTarget.style.opacity = '0.92';
                    }
                  }}
                  onMouseLeave={e => {
                    if (file?.auditId || file?.id) {
                      e.currentTarget.style.boxShadow = '0 2px 10px rgba(29,78,216,0.32)';
                      e.currentTarget.style.opacity = '1';
                    }
                  }}
                >
                  {/* Rocket icon */}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/>
                    <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/>
                    <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/>
                    <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/>
                  </svg>
                  Publish to User
                </button>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* Keyframe */}
      <style>{`@keyframes fpm-spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
};

// ── Small reusable footer outline button ──────────────────────────────────────
const FooterBtn = ({ icon, label, onClick, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      display: 'flex', alignItems: 'center', gap: 7,
      padding: '8px 14px',
      borderRadius: 8,
      border: '1px solid #e2e8f0',
      background: '#fff',
      fontSize: '13px', fontWeight: 500,
      color: disabled ? '#cbd5e1' : '#374151',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1,
      transition: 'background 0.15s, color 0.15s, border-color 0.15s',
      whiteSpace: 'nowrap',
    }}
    onMouseEnter={e => {
      if (!disabled) {
        e.currentTarget.style.background = '#f8fafc';
        e.currentTarget.style.borderColor = '#cbd5e1';
        e.currentTarget.style.color = '#0f172a';
      }
    }}
    onMouseLeave={e => {
      if (!disabled) {
        e.currentTarget.style.background = '#fff';
        e.currentTarget.style.borderColor = '#e2e8f0';
        e.currentTarget.style.color = '#374151';
      }
    }}
  >
    <Icon name={icon} size={14} />
    {label}
  </button>
);

export default FilePreviewModal;